// Global Instructions Rule Applied!
//
// User rights - the administrators' reads and writes (docs/USER_RIGHTS_DESIGN.md;
// function_user_rights_admin.sql, via /config/roles and
// /config/capabilities/:capabilityKey/overrides):
//   RoleModel                - one role (view__roles)
//   CapabilityOverridesModel - a capability's default roles and per-record overrides
//   CapabilityOverrideState  - one record's rule after a save
// and Configuration -> Users & Roles (/config/user-rights/*), below.
// Plain string ids, not .uuid(), per house rule.
import { z } from 'zod'
import { makeFieldKeys } from '../../core/utils/utilities.ts'

export const RoleModelSchema = z
  .object({
    roles__id: z.string(),
    roles__role_key: z.string(),
    roles__name: z.string(),
    roles__description: z.string().nullish(),
    roles__is_superuser: z.boolean(),
    roles__is_everyone: z.boolean(),
    roles__sort_order: z.number()
  })
  .strict()

export const CapabilityOverrideEntrySchema = z
  .object({
    capability_rules__scope_id: z.string(),
    prog__role_ids: z.array(z.string())
  })
  .strict()

export const CapabilityOverridesModelSchema = z
  .object({
    capabilities__capability_key: z.string(),
    prog__default_role_ids: z.array(z.string()),
    prog__overrides: z.array(CapabilityOverrideEntrySchema)
  })
  .strict()

export const CapabilityOverrideStateSchema = z
  .object({
    capabilities__capability_key: z.string(),
    capability_rules__scope_id: z.string(),
    prog__is_override: z.boolean(),
    prog__role_ids: z.array(z.string())
  })
  .strict()

// ── Configuration -> Users & Roles (design PR 3) ─────────────────────────────
//   UserRightsRoleSchema        - view__user_rights_roles
//   UserRightsUserSchema        - view__user_rights_users
//   UserRightsUserOptionSchema  - view__user_rights_user_options
//   UserRightsCapabilitySchema  - view__user_rights_capabilities (the matrix)
//   RoleSaved / RoleTrashed / RoleMemberState / CapabilityDefaultState - the writes

export const UserRightsRoleSchema = z
  .object({
    roles__id: z.string(),
    roles__role_key: z.string(),
    roles__name: z.string(),
    roles__description: z.string().nullish(),
    roles__is_superuser: z.boolean(),
    roles__is_everyone: z.boolean(),
    roles__sort_order: z.number(),
    /** Admin and Everyone: cannot be renamed, changed or removed. */
    prog__is_built_in: z.boolean(),
    /** Live users holding the role; always empty for Everyone. */
    prog__member_ids: z.array(z.string()),
    prog__member_count: z.number(),
    /** Live rules naming the role; a role with any cannot be removed. */
    prog__rule_count: z.number()
  })
  .strict()

export const UserRightsUserSchema = z
  .object({
    system_users__id: z.string(),
    system_users__email: z.string().nullish(),
    system_users__may_sign_in: z.boolean(),
    prog__name: z.string(),
    prog__role_ids: z.array(z.string())
  })
  .strict()

export const UserRightsUserOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    system_users__email: z.string()
  })
  .strict()

export const UserRightsCapabilitySchema = z
  .object({
    capabilities__id: z.string(),
    capabilities__capability_key: z.string(),
    capabilities__module: z.string(),
    capabilities__name: z.string(),
    capabilities__description: z.string().nullish(),
    capabilities__scope_table: z.string().nullish(),
    /** The Configuration menu item where its per-record rules are edited. */
    capabilities__overrides_menu_key: z.string().nullish(),
    capabilities__sort_order: z.number(),
    prog__module_label: z.string(),
    prog__label: z.string(),
    /** None: admins only. */
    prog__default_role_ids: z.array(z.string()),
    prog__override_count: z.number()
  })
  .strict()

export const RoleSavedSchema = z
  .object({
    roles__id: z.string(),
    roles__role_key: z.string(),
    roles__name: z.string()
  })
  .strict()

export const RoleTrashedSchema = z
  .object({
    roles__id: z.string(),
    roles__name: z.string(),
    prog__members_removed: z.number()
  })
  .strict()

export const RoleMemberStateSchema = z
  .object({
    roles__id: z.string(),
    system_users__id: z.string(),
    prog__is_member: z.boolean(),
    prog__member_count: z.number(),
    prog__summary: z.string()
  })
  .strict()

export const CapabilityDefaultStateSchema = z
  .object({
    capabilities__capability_key: z.string(),
    prog__default_role_ids: z.array(z.string()),
    prog__summary: z.string()
  })
  .strict()

export const RoleModelFields = makeFieldKeys(RoleModelSchema)
export const UserRightsRoleFields = makeFieldKeys(UserRightsRoleSchema)
export const UserRightsUserFields = makeFieldKeys(UserRightsUserSchema)
export const UserRightsCapabilityFields = makeFieldKeys(UserRightsCapabilitySchema)

export type RoleModel = z.infer<typeof RoleModelSchema>
export type CapabilityOverridesModel = z.infer<typeof CapabilityOverridesModelSchema>
export type CapabilityOverrideState = z.infer<typeof CapabilityOverrideStateSchema>
export type UserRightsRole = z.infer<typeof UserRightsRoleSchema>
export type UserRightsUser = z.infer<typeof UserRightsUserSchema>
export type UserRightsUserOption = z.infer<typeof UserRightsUserOptionSchema>
export type UserRightsCapability = z.infer<typeof UserRightsCapabilitySchema>
export type RoleSaved = z.infer<typeof RoleSavedSchema>
export type RoleTrashed = z.infer<typeof RoleTrashedSchema>
export type RoleMemberState = z.infer<typeof RoleMemberStateSchema>
export type CapabilityDefaultState = z.infer<typeof CapabilityDefaultStateSchema>
